import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCode,
  faLightbulb,
  faPlane,
  faBuilding,
  faCity,
} from '@fortawesome/free-solid-svg-icons';

const StorySection = styled.section`
  padding: 6rem 2rem;
  background: ${(props) => props.theme.colors.background};
`;

const Title = styled(motion.h2)`
  text-align: center;
  color: ${(props) => props.theme.colors.secondary};
  font-size: 2.5rem;
  margin-bottom: 4rem;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
    margin-bottom: 3rem;
  }
`;

const Ending = styled(motion.h2)`
  text-align: center;
  color: ${(props) => props.theme.colors.secondary};
  background: ${(props) => props.theme.colors.white};
  font-size: 1rem;
  padding: 2rem !important;
  line-height: 1.6;
  font-weight: 400;
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 15px;
  border: solid 3px ${(props) => props.theme.colors.primary};
`;
// Timeline container with vertical line
const Timeline = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;

  /* Vertical timeline line */
  &:before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 100%;
    background: ${(props) => props.theme.colors.primary};

    /* Move line to left side on mobile */
    @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
      left: 30px;
    }
  }
`;

const TimelineItem = styled(motion.div)<{ $isEven: boolean }>`
  display: flex;
  /* Alternate between left and right on desktop */
  justify-content: ${(props) => (props.$isEven ? 'flex-start' : 'flex-end')};
  padding: 2rem 0;
  width: 100%;
  position: relative;

  /* Stack items vertically on mobile */
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    justify-content: flex-start;
    padding-left: 80px;
  }
`;

const TimelineContent = styled.div<{ $isEven: boolean }>`
  width: 45%;
  padding: 2rem;
  background: ${(props) => props.theme.colors.white};
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;

  /* Full width on mobile */
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    width: 100%;
  }
`;

const TimelineDot = styled(motion.div)<{ $color: string }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: ${(props) => props.$color};
  border: 4px solid ${(props) => props.theme.colors.white};
  box-shadow: 0 0 0 4px ${(props) => props.$color}40;
  z-index: 1;

  /* Align dots to the left on mobile */
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    left: 30px;
  }
`;

const TimelineDate = styled.div`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.gray.medium};
  margin-bottom: 0.5rem;
`;

const TimelineTitle = styled.h3`
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 1rem;
  font-size: 1.3rem;
`;

const TimelineText = styled.p`
  color: ${(props) => props.theme.colors.gray.dark};
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const IllustrationContainer = styled(motion.div)`
  width: 120px;
  height: 120px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.gray.light};
  font-size: 2.5rem;
  color: ${(props) => props.theme.colors.secondary};
  transition: all 0.3s ease;

  /* Hover animation */
  &:hover {
    transform: scale(1.1);
    background: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.white};
  }
`;

interface TimelineEvent {
  date: string;
  title: string;
  description: string;
  color: string;
  icon: any; // FontAwesome icon
}

const timelineEvents: TimelineEvent[] = [
  {
    date: '2005 - 2010',
    title: 'Started Architecture Journey',
    description:
      'Torn between Software Engineering and Architecture for my B.A., I chose Architecture — and kept exploring everything else too. Blogging, freelancing, learning HTML/CSS, painting, 3D modeling… I dove into it all. In short: I was (and still am) in love with exploration.',
    color: '#FFD700',
    icon: faBuilding,
  },
  {
    date: '2012 - 2015',
    title: 'Designing Cities, then Interfaces!',
    description:
      'After a tough battle with illness, I returned to where I left off — starting my Master’s in Urban Design, and finishing it with the Outstanding Student Award. Not long after graduating, I traded blueprints for code and began my Front-End Development adventure.',
    color: '#429946',
    icon: faCity,
  },
  {
    date: '2015 - 2018',
    title: 'Where It All Started to Click!',
    description:
      'During this phase, I taught myself to code — diving into JavaScript, Git, responsive design, Figma, and more — while juggling freelance projects across design and development. It was messy, exciting, and full of growth. Bit by bit, I began blending my architectural thinking, artistic instincts, and technical skills into something uniquely mine. That’s when it all started to feel solid — like I wasn’t just learning tools, I was shaping a voice..',
    color: '#AA40F7',
    icon: faCode,
  },
  {
    date: '2018 - 2021',
    title: 'Breaking Boundaries, Shaping Change...',
    description:
      'I had the opportunity to collaborate with companies on impactful projects, honing my skills while never stopping my learning journey. But as I grew, the environment around me started feeling small — particularly for women. The limitations were palpable, and it only fueled my determination to push harder. I wasn’t just striving to succeed; I was striving to create a space where women could thrive and be recognized. Every challenge became an opportunity to change the narrative and make a difference.',
    color: '#426CA8',
    icon: faLightbulb,
  },
  {
    date: '2022 - NOW',
    title: 'And A Fresh Start in Germany!',
    description:
      'Relocated to Germany, immersing myself in new cultures and perspectives while continuing my journey as a solid Front-End Developer. This new chapter brought fresh challenges, new opportunities, and a chance to grow both as a developer and a global citizen.',
    color: '#FF69B4',
    icon: faPlane,
  },
];

const Story: React.FC = () => {
  return (
    <StorySection id='story'>
      {/* Animated title with fade-in and slide-up effect */}
      <Title
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        My Story
      </Title>
      <Timeline>
        {/* Map through timeline events and create timeline items */}
        {timelineEvents.map((event, index) => (
          <TimelineItem
            key={event.date}
            $isEven={index % 2 === 0}
            // Slide in from left/right animation
            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
          >
            <TimelineContent $isEven={index % 2 === 0}>
              {/* Animated icon container */}
              <IllustrationContainer
                whileHover={{ scale: 1.1 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <FontAwesomeIcon icon={event.icon} />
              </IllustrationContainer>
              <TimelineDate>{event.date}</TimelineDate>
              <TimelineTitle>{event.title}</TimelineTitle>
              <TimelineText>{event.description}</TimelineText>
            </TimelineContent>
            {/* Animated timeline dot */}
            <TimelineDot
              $color={event.color}
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: false }}
              transition={{ duration: 0.4, delay: index * 0.2 + 0.3 }}
            />
          </TimelineItem>
        ))}
      </Timeline>
      {/* Ending text with fade-in and slide-up effect */}
      <Ending
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false }}
        transition={{ duration: 0.6 }}
      >
        And, just when you think you’ve found an answer, you realize the journey
        is never really over. <br /> Being more of a walking question mark than
        a finished answer, I am still in the process of discovering — one step
        at a time.
      </Ending>
    </StorySection>
  );
};

export default Story;
